import jsPDF from "jspdf";
import { formatItalianDate } from "@/lib/dateFormat";

const CATS = [
  { key: "proteici", label: "Proteici", rgb: [166, 93, 71] },
  { key: "amidacei", label: "Amidacei", rgb: [139, 115, 85] },
  { key: "frutta", label: "Frutta", rgb: [176, 140, 77] },
  { key: "lipidici", label: "Lipidici", rgb: [99, 112, 91] },
];

const MEAL_STRIPES = [
  [166, 93, 71], [176, 140, 77], [99, 112, 91], [74, 98, 116], [139, 74, 47], [122, 74, 92],
];

const INK = [26, 26, 24];
const MUTED = [92, 90, 85];
const BORDER = [215, 210, 203];
const CREAM = [252, 250, 248];
const SOFT = [243, 239, 233];
const ACCENT = [166, 93, 71];
const RUST = [139, 74, 47];

const fmt = (n, d = 1) => Number(n || 0).toFixed(d).replace(".", ",");
const setColor = (doc, rgb) => { doc.setTextColor(rgb[0], rgb[1], rgb[2]); doc.setDrawColor(rgb[0], rgb[1], rgb[2]); };
const setFill = (doc, rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);

export function exportPlanPdf({ plan, client, totals, foodDb, massaMagraKg, proteineNobiliG, tdee = null, studio, logoAsset }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210, pageH = 297;
  const mX = 12;
  const contentW = pageW - mX * 2;

  // ===== STUDIO BRANDING BLOCK (optional, renders only when any field is set) =====
  const hasStudioText = !!(studio && (studio.studio_name || studio.address || studio.phone || studio.email || studio.website));
  const hasLogo = !!(logoAsset && logoAsset.dataUrl && logoAsset.width > 0);
  const hasStudio = hasStudioText || hasLogo;

  let cursorY = 10;
  if (hasStudio) {
    let logoW = 0, logoH = 0;
    if (hasLogo) {
      const maxDim = 18;
      const ratio = logoAsset.width / logoAsset.height;
      if (ratio >= 1) { logoW = maxDim; logoH = maxDim / ratio; }
      else { logoH = maxDim; logoW = maxDim * ratio; }
      try {
        // jsPDF auto-detects format from the data URL prefix.
        doc.addImage(logoAsset.dataUrl, mX, cursorY, logoW, logoH);
      } catch (err) {
        // addImage may throw for unsupported formats — safe to skip logo.
        console.warn("[export-pdf] skipping studio logo:", err?.message || err);
      }
    }
    const textX = mX + (hasLogo ? logoW + 4 : 0);
    let textY = cursorY + 4.2;
    if (studio?.studio_name) {
      setColor(doc, INK);
      doc.setFont("times", "normal");
      doc.setFontSize(12.5);
      doc.text(studio.studio_name, textX, textY);
      textY += 4.4;
    }
    if (studio?.address) {
      setColor(doc, MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(studio.address, textX, textY);
      textY += 3.4;
    }
    const contacts = [studio?.phone, studio?.email, studio?.website].filter(Boolean).join(" · ");
    if (contacts) {
      setColor(doc, MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(contacts, textX, textY);
      textY += 3.4;
    }
    const bottomOfText = textY - 1;
    const bottomOfLogo = cursorY + logoH;
    cursorY = Math.max(bottomOfText, bottomOfLogo, cursorY + 4) + 2;
    // Divider line
    setColor(doc, BORDER);
    doc.setLineWidth(0.2);
    doc.line(mX, cursorY, mX + contentW, cursorY);
    cursorY += 4;
  }

  // ===== PLAN HEADER =====
  const headerY = cursorY + 2;
  // Accent thin bar
  setFill(doc, ACCENT);
  doc.rect(mX, headerY - 2, 20, 1.2, "F");
  setColor(doc, MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("PIANO NUTRIZIONALE", mX, headerY + 3);
  setColor(doc, INK);
  doc.setFont("times", "normal");
  doc.setFontSize(22);
  doc.text(plan.name || "Piano nutrizionale", mX, headerY + 11);
  setColor(doc, MUTED);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  const genderLabel = client?.gender === "F" ? "Porzionario femminile" : "Porzionario maschile";
  const dateNow = formatItalianDate(new Date().toISOString().slice(0, 10));
  doc.text(`${client?.name || "Cliente"} · ${genderLabel} · ${dateNow}`, mX, headerY + 16);

  const headerBottom = headerY + 20;

  // ===== TOTALS CARD (bottom) =====
  const totalsH = 42;
  const totalsY = pageH - 12 - totalsH;

  // Available space for meal/workout cards — always single A4. Card height leaves room for
  // the extras line (when present) between title and portion grid.
  const mealsAreaTop = headerBottom + 2;
  const mealsAreaBottom = totalsY - 4;
  // Build sorted block list: meals + workouts by `time` ascending. Workouts
  // are rendered as a distinct blue card without portions / macros.
  const blocks = [
    ...(plan.meals || []).map((m) => ({ ...m, __type: "meal" })),
    ...(plan.workouts || []).map((w) => ({ ...w, __type: "workout" })),
  ].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  const blocksCount = Math.max(blocks.length, 1);
  const spacing = 3;
  const cardH = Math.max(20, Math.min(26, (mealsAreaBottom - mealsAreaTop - (blocksCount - 1) * spacing) / blocksCount));

  // Workout palette (matches on-screen editor/client colors).
  const WORKOUT_STRIPE = [63, 110, 138];   // #3F6E8A
  const WORKOUT_FILL   = [234, 243, 248];  // #EAF3F8
  const WORKOUT_INK    = [42, 76, 99];     // #2A4C63

  // ===== BLOCKS (meals + workouts sorted by time) =====
  let y = mealsAreaTop;
  blocks.forEach((block, idx) => {
    if (block.__type === "workout") {
      // Blue-tinted card. No portions grid, no macros — free-text only.
      setColor(doc, WORKOUT_STRIPE);
      setFill(doc, WORKOUT_FILL);
      doc.roundedRect(mX, y, contentW, cardH, 2.5, 2.5, "FD");
      setFill(doc, WORKOUT_STRIPE);
      doc.rect(mX, y, 2.2, cardH, "F");

      const innerX = mX + 6;
      // Title: "17:30 · Allenamento"
      setColor(doc, WORKOUT_INK);
      doc.setFont("times", "normal");
      doc.setFontSize(12);
      const wtTime = block.time ? `${block.time} · ` : "";
      doc.text(`${wtTime}Allenamento`, innerX, y + 6.8);

      // Right-side tag
      setColor(doc, WORKOUT_STRIPE);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      const tag = "TRAINING";
      const tagW = doc.getTextWidth(tag);
      doc.text(tag, mX + contentW - 6 - tagW, y + 6.8);

      // Body: free-text (wrapped, italic). Constrain to card height.
      const rawText = String(block.text || "").trim();
      if (rawText) {
        setColor(doc, WORKOUT_INK);
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9);
        const lineH = 3.6;
        const maxLines = Math.max(1, Math.floor((cardH - 9) / lineH));
        const lines = doc.splitTextToSize(rawText, contentW - 12).slice(0, maxLines);
        // If we truncated, hint with ellipsis on the last line.
        if (lines.length === maxLines && rawText.length > lines.join(" ").length) {
          const last = lines[lines.length - 1];
          lines[lines.length - 1] = last.slice(0, Math.max(0, last.length - 1)) + "…";
        }
        lines.forEach((ln, li) => {
          doc.text(ln, innerX, y + 11.5 + li * lineH);
        });
      }
      y += cardH + spacing;
      return;
    }

    // ---- MEAL card (original layout) ----
    const meal = block;
    const stripeRgb = MEAL_STRIPES[idx % MEAL_STRIPES.length];

    // Card frame with cream fill
    setColor(doc, BORDER);
    setFill(doc, CREAM);
    doc.roundedRect(mX, y, contentW, cardH, 2.5, 2.5, "FD");
    // Colored left stripe
    setFill(doc, stripeRgb);
    doc.rect(mX, y, 2.2, cardH, "F");

    // Title row: "6:30 · Colazione pre allenamento" — time before name, same style
    const innerX = mX + 6;
    setColor(doc, INK);
    doc.setFont("times", "normal");
    doc.setFontSize(12);
    const rawName = meal.name || "Pasto";
    const timePart = meal.time ? `${meal.time} · ` : "";
    // Reserve right side for per-meal macros summary
    const maxTitleW = contentW - 12 - 62; // 62mm reserved for macros row on same line if needed
    let titleStr = `${timePart}${rawName}`;
    while (doc.getTextWidth(titleStr) > maxTitleW && titleStr.length > timePart.length + 4) {
      titleStr = titleStr.slice(0, -1);
    }
    if (titleStr !== `${timePart}${rawName}`) titleStr += "…";
    doc.text(titleStr, innerX, y + 6.8);

    // Per-meal macros line (small, top-right on same row as title)
    let P = 0, C = 0, F = 0;
    for (const c of CATS) {
      const n = Number(meal.portions?.[c.key] || 0);
      const a = foodDb?.avg_per_portion?.[c.key] || {};
      P += n * (a.protein_g || 0);
      C += n * (a.carbs_g || 0);
      F += n * (a.fat_g || 0);
    }
    // Ad-hoc extra foods (per-meal only, not in porzionario) contribute
    // their own macros to the meal total while keeping only their name
    // visible in the layout below.
    for (const it of (meal.extras_items || [])) {
      P += Number(it.protein_g || 0);
      C += Number(it.carbs_g || 0);
      F += Number(it.fat_g || 0);
    }
    const cal = Math.round(P * 4 + C * 4 + F * 9);
    setColor(doc, MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    const macroStr = `${cal} kcal · prot. ${fmt(P, 0)}g · carb. ${fmt(C, 0)}g · gras. ${fmt(F, 0)}g`;
    doc.text(macroStr, mX + contentW - 6 - doc.getTextWidth(macroStr), y + 6.8);

    // Portions row (4 columns) — bottom of card. Bigger, denser layout.
    // Layout per column: [dot] LABEL on top row, then bigger VALUE with "porz." to the right.
    const gridLabelY = y + cardH - 8;
    const gridValueY = y + cardH - 2;
    const colW = (contentW - 10) / 4;
    for (let i = 0; i < CATS.length; i++) {
      const cat = CATS[i];
      const cx = innerX + i * colW;
      // colored dot + label (top of column)
      setFill(doc, cat.rgb);
      doc.circle(cx + 1.4, gridLabelY - 1.4, 1.05, "F");
      setColor(doc, MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.text(cat.label.toUpperCase(), cx + 3.7, gridLabelY);
      // big value
      setColor(doc, INK);
      doc.setFont("times", "normal");
      doc.setFontSize(15);
      const n = Number(meal.portions?.[cat.key] || 0);
      const vStr = String(n).replace(".", ",");
      doc.text(vStr, cx, gridValueY);
      const valueW = doc.getTextWidth(vStr);
      // "porz." right of value with generous fixed gap to prevent any overlap
      setColor(doc, MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text("porz.", cx + valueW + 2, gridValueY);
    }

    // Extras — merged single line between title and portions grid:
    // free-text `extras` first, then names of `extras_items` (comma-joined).
    // Only names are shown; the P/C/G values are already reflected in the
    // per-meal macro summary above and in the daily totals card.
    const extrasItemsNames = (meal.extras_items || [])
      .map((it) => String(it.name || "").trim())
      .filter(Boolean);
    const extrasText = [meal.extras && String(meal.extras).trim(), extrasItemsNames.join(", ")]
      .filter(Boolean)
      .join(" · ");
    if (extrasText && cardH >= 22) {
      const extrasY = y + 13;
      setColor(doc, ACCENT);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.text("EXTRA", innerX, extrasY);
      const tagW = doc.getTextWidth("EXTRA");
      setColor(doc, INK);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      const contentX = innerX + tagW + 3;
      const maxW = contentW - 12 - tagW - 3;
      let s = extrasText;
      while (doc.getTextWidth(s) > maxW && s.length > 8) s = s.slice(0, -1);
      if (s !== extrasText) s += "…";
      doc.text(s, contentX, extrasY);
    }

    y += cardH + spacing;
  });

  // ===== TOTALS CARD =====
  const tX = mX, tY = totalsY, tW = contentW, tH = totalsH;
  setFill(doc, INK);
  doc.roundedRect(tX, tY, tW, tH, 2.5, 2.5, "F");

  // Header
  doc.setTextColor(CREAM[0], CREAM[1], CREAM[2]);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("TOTALI GIORNALIERI", tX + 5, tY + 6);

  // Big calorie number
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  const kcalStr = String(totals?.calories || 0);
  doc.text(kcalStr, tX + 5, tY + 16);
  // IMPORTANT: measure the number's width WHILE the big font is still active,
  // otherwise "kcal" collapses onto the number.
  const kcalW = doc.getTextWidth(kcalStr);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("kcal", tX + 5 + kcalW + 2, tY + 16);
  const kcalUnitW = doc.getTextWidth("kcal");

  // Δ TDEE band — INLINE next to the big calorie number, vertically centered
  // against it. Mirrors on-screen MacrosSummary: ONE solid colored band that
  // wraps the whole delta wording (value · label · pct · TDEE reference),
  // so the delta reads as a single atomic piece of information.
  if (tdee && totals?.calories) {
    const kcal = totals.calories;
    const delta = kcal - tdee;
    const bandRgb = Math.abs(delta) <= 50
      ? [99, 112, 91]   // moss — mantenimento
      : delta > 0
        ? [166, 93, 71] // terracotta — surplus
        : [74, 124, 89];// green — deficit
    const bandLabel = Math.abs(delta) <= 50 ? "mantenimento" : delta > 0 ? "surplus" : "deficit";
    const sign = delta > 0 ? "+" : "";
    const pct = Math.round((delta / tdee) * 100);
    const label = `Δ ${sign}${delta} kcal · ${bandLabel} ${sign}${pct}% · vs TDEE ${tdee}`;
    doc.setFontSize(7.5);
    const bw = doc.getTextWidth(label) + 6;
    const pillX = tX + 5 + kcalW + 2 + kcalUnitW + 3;
    const pillY = tY + 10;
    setFill(doc, bandRgb);
    doc.roundedRect(pillX, pillY, bw, 6, 3, 3, "F");
    doc.setTextColor(CREAM[0], CREAM[1], CREAM[2]);
    doc.text(label, pillX + 3, pillY + 4.1);
  }

  // kcal/kg MM pill (top-right)
  if (massaMagraKg > 0) {
    const label = `${fmt((totals?.calories || 0) / massaMagraKg, 1)} kcal/kg MM`;
    doc.setFontSize(8);
    const bw = doc.getTextWidth(label) + 5;
    setFill(doc, ACCENT);
    doc.roundedRect(tX + tW - 5 - bw, tY + 10, bw, 6, 3, 3, "F");
    doc.setTextColor(CREAM[0], CREAM[1], CREAM[2]);
    doc.text(label, tX + tW - 5 - bw + 2.5, tY + 14.2);
  }

  // Colored progress bar (P/C/F proportions) — distinct hues for readability:
  // proteins = terracotta (red-orange), carbs = warm gold, fats = deep teal-blue.
  const P = totals?.protein_g || 0, C = totals?.carbs_g || 0, F = totals?.fat_g || 0;
  const totKcal = Math.max(P * 4 + C * 4 + F * 9, 1);
  const barY = tY + 20.5, barX = tX + 5, barW = tW - 10, barH = 1.8;
  setFill(doc, [58, 58, 55]);
  doc.rect(barX, barY, barW, barH, "F");
  const COLOR_PROT = [166, 93, 71];   // terracotta
  const COLOR_PROT_NOBILI = [139, 74, 47]; // deeper terracotta (subset of protein)
  const COLOR_CARB = [212, 165, 65];  // warm gold
  const COLOR_FAT = [74, 98, 116];    // deep teal-blue
  let acc = 0;
  const bars = [
    { rgb: COLOR_PROT, w: (P * 4 / totKcal) * barW },
    { rgb: COLOR_CARB, w: (C * 4 / totKcal) * barW },
    { rgb: COLOR_FAT,  w: (F * 9 / totKcal) * barW },
  ];
  bars.forEach(b => { setFill(doc, b.rgb); doc.rect(barX + acc, barY, Math.max(b.w, 0), barH, "F"); acc += b.w; });

  // Macro rows (compact) — proteine nobili right under proteine (subset visual link),
  // then carboidrati, then grassi.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  const rows = [
    { label: "Proteine", g: P, rgb: COLOR_PROT },
    { label: "Proteine nobili", g: proteineNobiliG || 0, rgb: COLOR_PROT_NOBILI },
    { label: "Carboidrati", g: C, rgb: COLOR_CARB },
    { label: "Grassi", g: F, rgb: COLOR_FAT },
  ];
  const rowsStartY = tY + 26;
  const rowStep = 3.6;
  rows.forEach((r, i) => {
    const ry = rowsStartY + i * rowStep;
    // dot
    setFill(doc, r.rgb);
    doc.circle(tX + 6, ry - 1, 0.7, "F");
    // label
    doc.setTextColor(CREAM[0], CREAM[1], CREAM[2]);
    doc.text(r.label, tX + 9, ry);
    // absolute
    doc.text(`${fmt(r.g, 1)}g`, tX + 55, ry);
    // relative pill
    if (massaMagraKg > 0) {
      const relStr = `${fmt(r.g / massaMagraKg, 2)} g/kg MM`;
      doc.setFontSize(7);
      const pw = doc.getTextWidth(relStr) + 3.5;
      setFill(doc, r.rgb);
      doc.roundedRect(tX + 80, ry - 2.4, pw, 3.2, 1.6, 1.6, "F");
      doc.setTextColor(CREAM[0], CREAM[1], CREAM[2]);
      doc.text(relStr, tX + 80 + 1.75, ry + 0.1);
      doc.setFontSize(7.5);
    }
  });

  if (!massaMagraKg || massaMagraKg <= 0) {
    doc.setTextColor(200, 195, 185);
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "italic");
    doc.text("Aggiungi un rilievo con massa magra per valori relativi.", tX + tW - 5 - 62, tY + tH - 3);
  }

  const filename = `Piano_${(plan.name || "nutrizionale").replace(/[^a-zA-Z0-9-_]+/g, "_")}_${(client?.name || "").replace(/\s+/g, "_")}.pdf`;
  doc.save(filename);
}
